'use client'

import { IconThumbsUpFill } from '@macarongfactory/web-icons'
import { Label, Text } from '@macarongfactory/web-ui'

import {
  type IKeywordStat,
  type IPlaceReviewSummary,
  type IPositiveReviewRate,
  type ISeoPlace,
} from '@/features/seoPlaceDetail/model'
import { IconAiSparkle } from '@/shared/assets/icons'

const TITLE = '마이클 실거래 고객 리뷰'
const FALLBACK_COMMENT =
  '마이클에 새롭게 입점한 정비소로, 실제 이용 고객의 리뷰가 쌓이고 있어요.'

const KEYWORD_LABEL_MAP: Record<string, string> = {
  kindness: '친절',
  spaceClean: '깔끔한 휴게실',
  accessible: '접근성',
  informative: '자세한 설명',
}

interface IPlaceReviewSummaryProps {
  place: ISeoPlace
  reviewSummary: IPlaceReviewSummary | null
  positiveReviewRate: IPositiveReviewRate | null
}

const PlaceReviewSummary = ({
  place,
  reviewSummary,
  positiveReviewRate,
}: IPlaceReviewSummaryProps) => {
  const reviewComment = place.reviewComment?.trim() || null
  const comment = reviewComment ?? FALLBACK_COMMENT

  const keywordChips = buildKeywordChips(reviewSummary?.keywords)
  const hasChips = positiveReviewRate !== null || keywordChips.length > 0

  return (
    <div className='flex flex-col gap-dimension-x3 rounded-dimension-x3 bg-background-neutral-weak p-dimension-x4'>
      <div className='flex items-center gap-dimension-x1'>
        <IconAiSparkle width={20} height={20} />
        <Text variant='body-1-semibold' color='text-foreground-brand-strong'>
          {TITLE}
        </Text>
      </div>

      {hasChips && (
        <div className='flex flex-wrap items-start gap-dimension-x1-5'>
          {positiveReviewRate !== null &&
            positiveReviewRate.positiveRate !== 0 && (
              <Label
                type='solid'
                variant='brand'
                size='medium'
                leadingIcon={<IconThumbsUpFill />}
                text={`긍정 리뷰 ${formatPercentage(positiveReviewRate.positiveRate)}%`}
              />
            )}
          {keywordChips
            .filter(chip => chip.percentage !== 0)
            .map(chip => (
              <Label
                key={chip.code}
                type='outline'
                variant='neutral'
                size='medium'
                text={`${chip.label} ${formatPercentage(chip.percentage)}%`}
              />
            ))}
        </div>
      )}

      <Text
        variant='body-1-medium'
        color='text-foreground-neutral-strong'
        className='whitespace-pre-line'
      >
        {comment}
      </Text>

      <Text variant='body-2-regular' color='text-foreground-neutral-assistive'>
        마이클 리뷰 자동 분석
      </Text>
    </div>
  )
}

interface IKeywordChip {
  code: string
  label: string
  percentage: number
}

const buildKeywordChips = (
  keywords: IKeywordStat[] | undefined
): IKeywordChip[] => {
  if (!Array.isArray(keywords) || keywords.length === 0) return []

  return keywords
    .map(({ code, percentage }) => {
      const label = KEYWORD_LABEL_MAP[code]
      if (!label) {
        console.warn('[PlaceReviewSummary] unknown keyword code', { code })
        return null
      }
      return { code, label, percentage }
    })
    .filter((chip): chip is IKeywordChip => chip !== null)
    .sort((a, b) => b.percentage - a.percentage)
}

const formatPercentage = (value: number): number => Math.round(value)

export default PlaceReviewSummary
